import React from "react";
import ExpandableContainer from "../../../configuration/steps/ExpandableContainer";
import Warning from "../custom-fields/icons/Warning";
import { Calculation } from "../../../../../src/application/Contracts";
import MatchingUnitModel = Calculation.MatchingUnitModel;
import InstallationType = Calculation.InstallationType;
import formModelStore from "../../../../store/FormModelStore";
import Field from "../../../../model/configurator/Field";
import BaseInputValidator from "../../../common/input-validators/BaseInputValidator";
import BaseInputMethodCalculationComponentInterface from "./BaseInputMethodCalculationComponentInterface";
import TWSCalculationApiService from "../../../../api-services/TWSCalculationApiService";
import PrimaryTankCalculationError = Calculation.PrimaryTankCalculationError;
import { CalculateMethod } from "./CalculateMethod";
import { ApartmentNumbersTable } from "./ApartmentNumbersTable";
import { CustomFieldsEnum } from "../../../../enums/CustomFieldsEnum";
import { RenderStepInputReadonlyField } from "../custom-fields/Helper";


interface SelfProps {
    apiService: TWSCalculationApiService | null,
    renderField: (field: any, additionalProps?: object, validator?: BaseInputValidator | undefined) => any,
    fields: { [key: string]: Field },
    matchingUnitModels: MatchingUnitModel[];
    equivalentInStandardApartments: number;
    selectedInstallationType: number;
    primaryTankCalculationError?: PrimaryTankCalculationError;
    primaryTankUnitCapacity: number;
    primaryTankStorage: number;
    primaryTankMadeOf: string;
    primaryTankFlowRate: number;
}

export class InstallationTypeInputMethod extends React.Component<SelfProps> implements BaseInputMethodCalculationComponentInterface {



    withoutPrimaryTankCalculationErrors = () => {
        return this.props.primaryTankCalculationError === undefined || this.props.primaryTankCalculationError == null
    }

    showFullPrimaryTankSection = (): boolean => {
        return this.props.matchingUnitModels.length > 0 && this.props.fields.primaryTankAvailableCapacity.value && this.withoutPrimaryTankCalculationErrors()
    }

    renderPrimaryCalculationError = () => {
        if (this.withoutPrimaryTankCalculationErrors()) {
            return <div></div>
        }
        let message = "";
        switch (this.props.primaryTankCalculationError) {
            case PrimaryTankCalculationError.PrimaryTankNotNeeded:
                message = __('Primary tank not needed!');
                break;
            case PrimaryTankCalculationError.PrimaryTankVolumeCapacityTooBig:
                message = __('Too big tank volume capacity')
                break;
        }
        return <div className={`step-table-warning error`}>
            <Warning />
            {message}
        </div>
    }

    temperatureItems = () => {
        return { items: [{ value: 60, text: "60 °C" }, { value: 55, text: "55 °C" },] }
    }

    render() {
        return (<React.Fragment>
            <div className={`step-row`}>
                <div className={`title`}>{__("Type of installation")}</div>
                {this.props.renderField(this.props.fields.installationType)}
            </div>
            <div className={`row row-no-gutter`}>
                <div className="column-6 column-xs-12">
                    <div className={`card`}>
                        <div className={`card-body`}>
                            <ExpandableContainer arrowFirst={true} label={__('Calculate method')}
                                initialOpen={true}>
                                <CalculateMethod selectedInstallationType={this.props.selectedInstallationType}
                                    renderField={this.props.renderField}
                                    fields={this.props.fields} />
                            </ExpandableContainer>
                            {this.showForInstallationType(InstallationType.ApartmentsBlocks) && (
                                <ExpandableContainer arrowFirst={true} label={__('Appt numbers')}
                                    initialOpen={true}
                                    additionalClasses={['zero-padding']}>
                                    <ApartmentNumbersTable renderField={this.props.renderField}
                                        fields={this.props.fields}
                                        equivalentInStandardApartments={this.props.equivalentInStandardApartments} />
                                </ExpandableContainer>)}
                            <ExpandableContainer additionalClasses={['primary-tank']} arrowFirst={true}
                                initialOpen={true}
                                label={__('Primary tank')}>
                                <div className={`step-inputs-row`}>
                                    {this.props.renderField(this.props.fields.primaryTankAvailableCapacity)}
                                    {this.props.renderField(this.props.fields.primaryTankAquaEfficiency, { renderLabel: false })}
                                </div>
                                {this.renderPrimaryCalculationError()}
                                {this.showFullPrimaryTankSection() && (
                                    <React.Fragment>
                                        <div className={'line-separator'} />
                                        <div className={`step-inputs-row grid-3`}>
                                            {this.props.renderField(this.props.fields.unitCapacity, { value: this.props.primaryTankUnitCapacity })}
                                            {this.props.renderField(this.props.fields.primaryStorage, { value: this.props.primaryTankStorage })}
                                        </div>
                                        <div className={'line-separator'} />
                                        <div className={`step-inputs-row`}>
                                            {this.props.renderField(this.props.fields.primaryTankChargingPtDt)}
                                            {this.props.fields.primaryTankChargingPtDt.value > 0 && RenderStepInputReadonlyField("Primary flow rate", this.props.primaryTankFlowRate, "m³/h")}
                                            <div />
                                            <div>
                                                {this.renderTankLoadingTime()}
                                            </div>
                                        </div>
                                    </React.Fragment>
                                )}
                            </ExpandableContainer>
                        </div>
                    </div>
                </div>
                <div className={`column-6 column-xs-12`}>
                    <div className={`card`}>
                        <div className={`card-body`}>
                            <ExpandableContainer arrowFirst={true} label={__('Matching units')} initialOpen={true}
                                additionalClasses={['zero-padding', this.getLongTankLoadingTimeClass()]}>
                                {this.props.renderField(this.props.fields.matchingUnit, { additionalModel: this.props.matchingUnitModels })}
                            </ExpandableContainer>
                        </div>
                    </div>
                </div>
            </div>
        </React.Fragment>)
    }

    renderTankLoadingTime = () => {
        const modelWithTank = this.props.matchingUnitModels.find(x => x.hasPrimaryTank);

        if (modelWithTank === undefined || modelWithTank.loadTimeWithRecycling === undefined)
            return;

        return (
            <div className={this.getLongTankLoadingTimeClass()}>
                {RenderStepInputReadonlyField("Tank loading time", modelWithTank.loadTimeWithRecyclingReadable)}
            </div>
        );
    }

    getLongTankLoadingTimeClass = (): string => {
        const modelWithTank = this.props.matchingUnitModels.find(x => x.hasPrimaryTank);

        if (modelWithTank === undefined || modelWithTank.loadTimeWithRecycling === undefined)
            return '';

        const timeLongerThan8Hours = modelWithTank.loadTimeWithRecycling > 480;
        return timeLongerThan8Hours ? 'high-tank-loading-time-wrapper' : '';
    }

    isValidFormModel = (selectedType: number): boolean => {
        if (this.showForInstallationType(InstallationType.ApartmentsBlocks, selectedType)) {
            return formModelStore.formModel.inputMethod.installationTypeCalculation.tDhw.value;
        }
        if (this.showForInstallationType(InstallationType.Internats, selectedType)) {
            return formModelStore.formModel.inputMethod.installationTypeCalculation.numberOfBeds.value &&
                formModelStore.formModel.inputMethod.installationTypeCalculation.numberOfShowerRooms.value &&
                formModelStore.formModel.inputMethod.installationTypeCalculation.numberOfShowersPerShowerRoom.value &&
                formModelStore.formModel.inputMethod.installationTypeCalculation.showerConsumption.value &&
                formModelStore.formModel.inputMethod.installationTypeCalculation.showerMeanDuration.value;
        }
        if (this.showForInstallationType(InstallationType.Restaurants, selectedType)) {
            return formModelStore.formModel.inputMethod.installationTypeCalculation.numberMealsBreakfast.value &&
                formModelStore.formModel.inputMethod.installationTypeCalculation.numberMealsLunch.value &&
                formModelStore.formModel.inputMethod.installationTypeCalculation.numberMealsDinner.value
        }
        if (this.showForInstallationType(InstallationType.Hotels, selectedType) || this.showForInstallationType(InstallationType.Retirement, selectedType) || this.showForInstallationType(InstallationType.Campings, selectedType)) {
            return formModelStore.formModel.inputMethod.installationTypeCalculation.numberOfRooms.value
        }
        if (this.showForInstallationType(InstallationType.Hospitals, selectedType)) {
            return formModelStore.formModel.inputMethod.installationTypeCalculation.numberOfRooms.value &&
                formModelStore.formModel.inputMethod.installationTypeCalculation.bedsPerRoom.value
        }
        if (this.showForInstallationType(InstallationType.SportCenters, selectedType)) {
            return formModelStore.formModel.inputMethod.installationTypeCalculation.sportCenterGymnasiumNumberOfSportHalls.value &&
                formModelStore.formModel.inputMethod.installationTypeCalculation.sportCenterStadiumNumberOfSportGrounds.value &&
                formModelStore.formModel.inputMethod.installationTypeCalculation.numberOfShowerRooms.value &&
                formModelStore.formModel.inputMethod.installationTypeCalculation.numberOfShowersPerShowerRoom.value &&
                formModelStore.formModel.inputMethod.installationTypeCalculation.showerConsumption.value &&
                formModelStore.formModel.inputMethod.installationTypeCalculation.showerMeanDuration.value;
        }
        return true;
    }

    showForInstallationType = (installationType: InstallationType, selectedType: number | undefined = undefined): boolean => {
        return (selectedType ?? this.props.selectedInstallationType) === installationType;
    }

    canProceed = (): boolean => {
        return this.props.matchingUnitModels.length > 0 && this.props.fields['matchingUnit'].value !== null && this.props.fields['matchingUnit'].value !== undefined
    }
}